/*
 * 万圣节转盘落地页（sections/halloween-landing.liquid）的全部交互：
 * 活动倒计时、转盘抽奖、中奖卡与个人倒计时、置顶倒计时条、分享专属链接换额外抽奖、鞋款区展开和选购面板里的页内加购。
 * 抽奖结果只来自主题之外的抽奖服务（根元素 data-api），本文件里没有概率，也不自己出奖。
 * 无依赖、不往 window 挂东西；所有提示都写进页面内的 aria-live 文字，不用 alert。
 */
(() => {
  'use strict';

  const doc = document;
  const root = doc.querySelector('[data-hw]');
  if (!root) return;

  const STORE_KEY = 'nx-hw26';
  // 专属分享标识单独一个 key：STORE_KEY 的读取假定里面一定是完整的中奖结果，没中过奖的人也能分享
  const REF_KEY = 'nx-hw26-ref';
  const VISIT_KEY = 'nx-hw26-visit';
  // 防刷用的设备 ID：第一次打开时随机生成存在浏览器里，抽奖时带给服务（同一设备短时间换邮箱要验证码）
  const DEVICE_KEY = 'nx-hw26-device';
  // 分享按钮每天（美东自然日）最多点 SHARE_PER_DAY 次，按设备计。加转的上限在服务端，这里只限行为
  const SHARE_COUNT_KEY = 'nx-hw26-shares';
  const SHARE_PER_DAY = 3;
  const REF_RE = /^[a-z0-9]{6,20}$/i;
  const PRIZES = { 10: '10% off', 15: '15% off', 20: '20% off', bogo: 'BOGO Free' };
  // 后缀只能是字母（2–24 位），域名每段是字母数字或连字符
  const EMAIL_RE = /^[^\s@]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,24}$/i;
  // 常见邮箱服务商拼错的域名和 .com 拼错的后缀：格式虽然合法，但几乎都是手误，收不到码，先请顾客确认
  const DOMAIN_TYPOS = {
    gail: 'gmail', gmai: 'gmail', gmal: 'gmail', gmial: 'gmail', gamil: 'gmail', gnail: 'gmail', gmaill: 'gmail',
    gmali: 'gmail', gmil: 'gmail', gemail: 'gmail', yaho: 'yahoo', yahooo: 'yahoo', yhoo: 'yahoo', yahho: 'yahoo',
    hotmal: 'hotmail', hotmial: 'hotmail', hotmai: 'hotmail', hotmil: 'hotmail', outlok: 'outlook', outloo: 'outlook',
    outllok: 'outlook', iclod: 'icloud', icoud: 'icloud', iclould: 'icloud', aoll: 'aol'
  };
  const TLD_TYPOS = ['con', 'cmo', 'coms', 'comm', 'cm', 'om', 'vom', 'xom', 'ocm', 'cpm', 'comn', 'coom', 'clm', 'cim'];
  // 返回建议的正确邮箱；看不出手误就返回空串（只看「名.后缀」两段的域名，带子域名的不猜）
  const suggestEmail = (email) => {
    const at = email.lastIndexOf('@');
    const domain = email.slice(at + 1).toLowerCase();
    const parts = domain.split('.');
    if (parts.length !== 2) return '';
    const name = DOMAIN_TYPOS[parts[0]] || parts[0];
    // .com 的手误后缀改回 com；gmail 只有 .com
    const tld = TLD_TYPOS.includes(parts[1]) || name === 'gmail' ? 'com' : parts[1];
    const fixed = `${name}.${tld}`;
    return fixed === domain ? '' : `${email.slice(0, at)}@${fixed}`;
  };
  // 转盘压到 3 秒内（用户 2026-10-16）：匀速提到每秒 3 圈，拿到结果后只多转 1 圈。
  // 减速段时长 = 3 × 距离 / 匀速，距离 360～720° → 1～2 秒；再加上等服务返回的时间（一般 1 秒左右）
  const CRUISE_SPEED = 1800; // 等结果时的匀速，度 / 秒
  const MIN_TRAVEL = 180; // 拿到结果后至少再转的角度，太短会像急刹
  const REQUEST_TIMEOUT = 15000;

  const $ = (selector, scope = root) => scope.querySelector(selector);
  const $$ = (selector, scope = root) => Array.from(scope.querySelectorAll(selector));
  const routeRoot = () => window.Shopify?.routes?.root ?? '/';
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const mod = (value, base) => ((value % base) + base) % base;
  const pad = (number) => String(number).padStart(2, '0');
  const isPrize = (prize) => Object.prototype.hasOwnProperty.call(PRIZES, prize);
  // 埋点：只报业务结果，GA 由 assets/nx-halloween-analytics.js 接收；它没加载时无人监听，什么也不发生。参数里不放邮箱、码、ref
  const track = (name, params = {}) => doc.dispatchEvent(new CustomEvent('nx-hw:track', { detail: { name, params } }));

  const readJSON = (node, fallback) => {
    try {
      return JSON.parse(node.textContent) ?? fallback;
    } catch (error) {
      return fallback;
    }
  };

  const scrollToNode = (node) => {
    node?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  /* ------------------------------------------------------------ 鞋款区 */
  const initMore = () => {
    const button = $('[data-hw-more]');
    const grid = $('[data-hw-grid]');
    if (!button || !grid) return;

    const label = $('[data-hw-more-label]', button);
    button.addEventListener('click', () => {
      const open = grid.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? label.dataset.lessLabel : label.dataset.moreLabel;
      // 收起后列表变短，把视口带回鞋款区顶部，免得停在后面的模块上
      if (!open) scrollToNode($('[data-hw-shoes]'));
    });
  };

  /* ------------------------------------------------------------ 问答 */
  // <details> 的开合是瞬时的，这里给高度补一段过渡。不支持 Web Animations 或访客要求减少动效时走原生行为
  /* ------------------------------------------------------------ 邮箱框：填了内容后图标变白 */
  const initEmailIcon = () => {
    const input = $('[data-hw-email]');
    const field = input?.closest('.hw-email__field');
    if (!field) return;
    const sync = () => field.classList.toggle('is-filled', input.value.trim() !== '');
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    sync();
    // 本地记住的邮箱由后面的初始化代码回填，回填后再同步一次
    window.setTimeout(sync, 0);
  };

  const initFaq = () => {
    $$('.hw-faq__item').forEach((item) => {
      const summary = $('summary', item);
      if (!summary || typeof item.animate !== 'function') return;

      const answer = $('.hw-faq__answer', item);
      let running = null;
      let fading = null;
      summary.addEventListener('click', (event) => {
        if (reducedMotion()) return;
        event.preventDefault();

        // 动画进行中再点：从当前高度掉头
        const from = item.offsetHeight;
        const opening = !item.open || item.classList.contains('is-closing');
        running?.cancel();
        fading?.cancel();

        item.classList.toggle('is-closing', !opening);
        let to;
        if (opening) {
          item.open = true;
          to = item.offsetHeight;
        } else {
          // 量收起后的高度：关一下再开回来，同一帧内不会被画出来
          item.open = false;
          to = item.offsetHeight;
          item.open = true;
        }

        // 高度 180ms；答案文字单独淡入淡出、比高度更快，展开时文字先出来，收起时文字先消失
        item.style.overflow = 'hidden';
        const animation = item.animate(
          { height: [`${from}px`, `${to}px`] },
          { duration: 180, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
        );
        fading = answer?.animate(
          { opacity: opening ? [0, 1] : [1, 0] },
          { duration: opening ? 120 : 90, easing: 'ease-out', fill: 'forwards' }
        ) ?? null;
        running = animation;
        animation.onfinish = () => {
          if (!opening) item.open = false;
          item.classList.remove('is-closing');
          item.style.overflow = '';
          fading?.cancel();
          running = null;
          fading = null;
        };
      });
    });
  };

  /* ------------------------------------------------------------ 礼品卡条款气泡 */
  // 网页端悬停 / 聚焦由样式负责；这里处理点按开合（触屏主要靠它），点别处或按 Esc 收起
  const initTips = () => {
    const tips = $$('[data-hw-tip]');
    if (!tips.length) return;
    const setOpen = (tip, open) => {
      tip.classList.toggle('is-open', open);
      $('[data-hw-tip-btn]', tip)?.setAttribute('aria-expanded', String(open));
    };
    // 气泡挂在标题框下方右对齐，小三角要对准 i 图标：按图标中心到标题框右缘的距离算
    const placeArrows = () => {
      tips.forEach((tip) => {
        const button = $('[data-hw-tip-btn]', tip);
        const box = tip.closest('h3, h2, p') || tip.parentElement;
        if (!button || !box) return;
        const iconCenter = button.getBoundingClientRect().left + button.offsetWidth / 2;
        const right = box.getBoundingClientRect().right - iconCenter - 7; // 7 = 小三角（12 宽、转 45°）一半多一点
        tip.style.setProperty('--hw-tip-arrow', `${Math.max(8, Math.round(right))}px`);
      });
    };
    placeArrows();
    window.addEventListener('resize', placeArrows);
    doc.fonts?.ready.then(placeArrows);

    tips.forEach((tip) => {
      $('[data-hw-tip-btn]', tip)?.addEventListener('mouseenter', placeArrows);
      $('[data-hw-tip-btn]', tip)?.addEventListener('click', (event) => {
        event.stopPropagation();
        setOpen(tip, !tip.classList.contains('is-open'));
      });
    });
    doc.addEventListener('click', (event) => {
      tips.forEach((tip) => {
        if (!tip.contains(event.target)) setOpen(tip, false);
      });
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') tips.forEach((tip) => setOpen(tip, false));
    });
  };

  const sizedImage = (src, width) => {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.href;
    } catch (error) {
      return src;
    }
  };

  // 卡面只有 Add 按钮，点它打开本卡的选购面板（<dialog>）；颜色、尺码、加购都在面板里。
  // 价格和图在卡面、面板里各有一份，换颜色时一起更新
  /* ------------------------------------------------------------ 尺码表弹层（全页共用一个） */
  const chart = $('[data-hw-chart]');
  const openChart = (desktop, mobile) => {
    if (!chart || !desktop) return;
    const d = $('[data-hw-chart-d]', chart);
    const m = $('[data-hw-chart-m]', chart);
    if (d && d.getAttribute('src') !== desktop) d.src = desktop;
    if (m && m.getAttribute('src') !== (mobile || desktop)) m.src = mobile || desktop;
    // 每次打开从 1 倍开始
    window.nxZoomReset?.($('[data-nx-zoom]', chart));
    if (typeof chart.showModal === 'function') chart.showModal();
    else chart.setAttribute('open', '');
  };
  const closeChart = () => {
    if (!chart) return;
    if (typeof chart.close === 'function') chart.close();
    else chart.removeAttribute('open');
  };
  $('[data-hw-chart-close]', chart || root)?.addEventListener('click', closeChart);
  // 点白框以外的遮罩关闭：遮罩上的点击落在 dialog 元素自身
  chart?.addEventListener('click', (event) => {
    if (event.target === chart) closeChart();
  });

  const initCard = (card) => {
    const addButton = $('[data-hwp-add]', card);
    const openButton = $('[data-hwp-open]', card);
    const sheet = $('[data-hwp-sheet]', card);
    const variants = readJSON($('[data-hwp-variants]', card), []);
    if (!addButton || !openButton || !sheet || !Array.isArray(variants) || !variants.length) return;

    const labels = {
      add: card.dataset.hwpAddLabel,
      adding: card.dataset.hwpAddingLabel,
      soldOut: card.dataset.hwpSoldoutLabel,
      error: card.dataset.hwpError
    };
    const priceNodes = $$('[data-hwp-price]', card);
    const compareNodes = $$('[data-hwp-compare]', card);
    const imageNodes = $$('[data-hwp-img]', card);
    const messageNode = $('[data-hwp-msg]', card);
    // 没选尺码这类选项提示显示在选项下方、数量上方；加购失败等报错仍在按钮上方
    const promptNode = $('[data-hwp-prompt-msg]', card) ?? messageNode;
    // 每个选项一组按钮，data-hwp-option 是选项序号；颜色组带 data-hwp-color，换颜色时会清掉买不到的其它选项
    const groups = $$('[role="group"][data-hwp-option]', card).map((node) => ({
      node,
      index: Number(node.dataset.hwpOption),
      isColor: node.hasAttribute('data-hwp-color'),
      buttons: $$('[data-hwp-value]', node)
    }));
    const colorGroup = groups.find((group) => group.isColor);
    const qtyInput = $('[data-hwp-qty]', card);
    const qtySteps = $$('[data-hwp-qty-step]', card);
    const qtyMax = Number(qtyInput?.max) || 10;
    const handle = $('a[href*="/products/"]', card)?.getAttribute('href').match(/\/products\/([^/?#]+)/)?.[1] || '';

    // 每个选项一位，null = 还没选；单变体商品是空数组，任何变体都算匹配
    const selection = new Array(Number(card.dataset.hwpOptions) || 0).fill(null);
    if (colorGroup) {
      const pressed = colorGroup.buttons.find((button) => button.getAttribute('aria-pressed') === 'true') ?? colorGroup.buttons[0];
      selection[colorGroup.index] = pressed?.dataset.hwpValue ?? null;
    }

    const matches = (variant, picked) => picked.every((value, index) => value === null || variant.options[index] === value);
    const sellable = (picked) => variants.some((variant) => variant.available && matches(variant, picked));
    const withValue = (index, value) => selection.map((current, position) => (position === index ? value : current));

    let busy = false;
    let shownImage = null;
    // 页面上先显示商品主图；顾客在面板里点了颜色之后才换成该颜色变体的图
    let imageFollowsColor = false;

    const say = (text, isError = false, node = messageNode) => {
      [messageNode, promptNode].forEach((target) => {
        const show = target === node && Boolean(text);
        target.textContent = show ? text : '';
        target.classList.toggle('is-error', show && isError);
      });
    };

    const render = () => {
      groups.forEach((group) => {
        group.buttons.forEach((button) => {
          const value = button.dataset.hwpValue;
          button.setAttribute('aria-pressed', String(selection[group.index] === value));
          // 颜色总能点（换颜色会顺带清掉买不到的尺码）；其它选项在当前组合下买不到就置灰
          if (group.isColor) return;
          const ok = sellable(withValue(group.index, value));
          button.disabled = !ok;
          button.title = ok ? '' : labels.soldOut;
        });
      });

      // 没选齐时用当前颜色下的一个变体来显示价格和图，优先可售的
      const preview =
        variants.find((variant) => variant.available && matches(variant, selection)) ??
        variants.find((variant) => matches(variant, selection)) ??
        variants[0];
      priceNodes.forEach((node) => {
        node.textContent = preview.price;
      });
      compareNodes.forEach((node) => {
        node.textContent = preview.compare_at_price ?? '';
        node.hidden = !preview.compare_at_price;
      });
      if (imageFollowsColor && preview.featured_image && preview.featured_image !== shownImage) {
        shownImage = preview.featured_image;
        imageNodes.forEach((node) => {
          // 面板里的小图没有 srcset，只换 src
          if (node.hasAttribute('srcset')) {
            node.srcset = [300, 600, 900].map((width) => `${sizedImage(shownImage, width)} ${width}w`).join(', ');
            node.src = sizedImage(shownImage, 600);
          } else {
            node.src = sizedImage(shownImage, 300);
          }
        });
      }

      if (!busy) {
        const canBuy = sellable(selection);
        addButton.disabled = !canBuy;
        addButton.classList.remove('is-adding');
        addButton.textContent = canBuy ? labels.add : labels.soldOut;
      }
    };

    // 数量：读的时候收回到 1～上限之间，手输小数、空值、超范围都按边界算
    const readQty = () => Math.min(qtyMax, Math.max(1, Math.floor(Number(qtyInput?.value)) || 1));
    const setQty = (value) => {
      if (!qtyInput) return;
      qtyInput.value = String(value);
      qtySteps.forEach((button) => {
        button.disabled = Number(button.dataset.hwpQtyStep) < 0 ? value <= 1 : value >= qtyMax;
      });
    };
    qtySteps.forEach((button) => {
      button.addEventListener('click', () => setQty(Math.min(qtyMax, Math.max(1, readQty() + Number(button.dataset.hwpQtyStep)))));
    });
    qtyInput?.addEventListener('change', () => setQty(readQty()));

    // 不支持 <dialog> 的旧浏览器退回非模态显示（样式里 .hw-sheet[open] 一样固定在底部）
    const canModal = typeof sheet.showModal === 'function';
    const openSheet = () => {
      say('');
      setQty(1);
      if (canModal) sheet.showModal();
      else sheet.setAttribute('open', '');
    };
    const closeSheet = () => {
      if (canModal) sheet.close();
      else sheet.removeAttribute('open');
    };

    openButton.addEventListener('click', openSheet);
    $('[data-hwp-sizechart]', card)?.addEventListener('click', (event) => {
      const { chartD, chartM } = event.currentTarget.dataset;
      openChart(chartD, chartM);
    });
    $('[data-hwp-close]', card)?.addEventListener('click', closeSheet);
    // 点面板以外的遮罩关闭：遮罩上的点击落在 dialog 元素自身
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) closeSheet();
    });

    groups.forEach((group) => {
      group.buttons.forEach((button) => {
        button.addEventListener('click', () => {
          selection[group.index] = button.dataset.hwpValue;
          // 换颜色后原来选的尺码可能在新颜色下缺货，退回未选，不让顾客带着买不了的组合去点加购
          if (group.isColor) {
            imageFollowsColor = true;
            groups.forEach((other) => {
              if (!other.isColor && selection[other.index] !== null && !sellable(selection)) selection[other.index] = null;
            });
          }
          say('');
          render();
        });
      });
    });

    addButton.addEventListener('click', async () => {
      if (busy) return;
      const missing = groups.find((group) => selection[group.index] === null);
      if (missing) {
        track('hw_add_to_cart_click', { product_handle: handle, button_state: 'choose' });
        say(missing.node.dataset.hwpPrompt || 'Select an option', true, promptNode);
        missing.buttons.find((button) => !button.disabled)?.focus();
        return;
      }
      const variant = variants.find((candidate) => matches(candidate, selection));
      if (!variant || !variant.available) return;
      // price 是格式化好的单价字符串，埋点脚本换算成 value / currency
      const line = { product_handle: handle, variant_id: variant.id, quantity: readQty(), price: variant.price };
      track('hw_add_to_cart_click', { ...line, button_state: 'ready' });

      busy = true;
      addButton.disabled = true;
      // 加购中：前面转圈，ADDING 不动，后面的点按 . → .. → ... 循环出现，样式见 .hw-adding
      const loading = doc.createElement('span');
      loading.className = 'hw-adding';
      const spinner = doc.createElement('i');
      spinner.className = 'hw-adding__spin';
      spinner.setAttribute('aria-hidden', 'true');
      const word = doc.createElement('span');
      word.className = 'hw-adding__text';
      word.textContent = String(labels.adding).replace(/[.…]+$/, '');
      const dots = doc.createElement('span');
      dots.className = 'hw-adding__dots';
      dots.setAttribute('aria-hidden', 'true');
      [0, 1, 2].forEach((index) => {
        const dot = doc.createElement('span');
        dot.textContent = '.';
        dot.style.setProperty('--i', String(index));
        dots.append(dot);
      });
      word.append(dots);
      loading.append(spinner, word);
      addButton.classList.add('is-adding');
      addButton.replaceChildren(loading);
      say('');
      let response = null;
      try {
        response = await fetch(`${routeRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: line.quantity }], sections: 'cart-drawer' })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || `http_${response.status}`);
        track('hw_add_to_cart_success', line);
        // 与 nx-lp-shop.js 相同：把抽屉片段交给 Impulse CartDrawer 重建，再唤起抽屉
        const markup = data.sections?.['cart-drawer'];
        // 先收起面板：模态 dialog 开着时页面其余部分是 inert 的，抽屉点不了
        closeSheet();
        if (markup) doc.dispatchEvent(new CustomEvent('cart:build', { detail: { markup } }));
        doc.dispatchEvent(new CustomEvent('cart:open'));
      } catch (error) {
        console.error('[nx-halloween] Add to cart error:', error);
        // 成功事件已发出后（抽屉重建等环节）再出错不算加购失败
        if (!response?.ok) track('hw_add_to_cart_fail', { ...line, reason: String(error?.message || 'network_error').slice(0, 100) });
        say(labels.error, true);
      } finally {
        busy = false;
        render();
      }
    });

    render();
  };

  /* ------------------------------------------------------------ 活动倒计时 */
  // 截止时间来自 section 设置（ISO 时间）。过了截止时间数字停在 00；时间解析不出来就保留页面里写的样子
  const initCampaign = () => {
    const box = $('[data-hw-campaign]');
    if (!box) return;
    const end = Date.parse(box.dataset.hwCampaign);
    if (!Number.isFinite(end)) return;

    // 标题行按美东时间写，与规则里的 ET 一致，不随访客所在时区变
    const label = $('[data-hw-campaign-label]', box);
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).formatToParts(end);
      const part = (type) => parts.find((item) => item.type === type)?.value ?? '';
      if (label && part('month') && part('day')) {
        label.textContent = `End ${part('month').toUpperCase()} ${part('day')}• ${part('hour')}:${part('minute')} ${part('dayPeriod')} ET`;
      }
    } catch (error) {
      /* 没有 Intl 时区数据的浏览器保留页面里写的标题 */
    }

    const cells = ['d', 'h', 'm', 's'].map((key) => $(`[data-hw-campaign-part="${key}"]`, box));
    let timer = 0;
    const tick = () => {
      const total = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const values = [Math.floor(total / 86400), Math.floor((total % 86400) / 3600), Math.floor((total % 3600) / 60), total % 60];
      cells.forEach((cell, index) => {
        if (cell) cell.textContent = pad(values[index]);
      });
      if (total <= 0) window.clearInterval(timer);
    };
    timer = window.setInterval(tick, 1000);
    tick();
  };

  // 鞋款区和活动倒计时不依赖转盘：先初始化，转盘区缺节点或抽奖服务出问题都不影响它们
  initMore();
  initFaq();
  initEmailIcon();
  initTips();
  initCampaign();
  $$('[data-hwp]').forEach(initCard);

  /* ------------------------------------------------------------ 转盘区节点 */
  const form = $('[data-hw-form]');
  const emailInput = $('[data-hw-email]');
  const consentBox = $('[data-hw-consent]');
  // 表单里的 SPIN TO WIN 和盘心的 SPIN 都提交同一张表单，禁用状态一起同步
  const spinButtons = $$('[data-hw-spin-btn]');
  const spinButton = spinButtons[0];
  const disc = $('[data-hw-disc]');
  const card = $('[data-hw-card]');
  if (!form || !emailInput || !spinButton || !disc || !card) return;

  const api = (root.dataset.api || '').trim().replace(/\/+$/, '');
  // 会随活动安排变的文案来自 section 设置（data-hw-config）；下面这几句操作反馈固定写在这里，
  // 因为 theme check 限制单个 section 的设置数，全做成设置会超
  const text = {
    emailEmpty: 'Enter your email to spin.',
    emailInvalid: 'Please enter a valid email.',
    emailTypo: 'Please check your email. Did you mean [email]?',
    consentRequired: 'Please agree to receive Nexbie marketing emails to spin.',
    error: 'Something went wrong. Please try again in a moment.',
    shareNeedEmail: 'Enter your email above first.',
    shareCopied: 'Link copied! Pick an app and paste it to a friend.',
    shareCopyFailed: 'Select the link above to copy it.',
    shareLoading: 'Getting your link…',
    shareLimit: "You've shared 3 times today. Come back tomorrow to share again.",
    // 防刷验证码弹窗
    verifyEnter: 'Enter the 6-digit code from your email.',
    verifyInvalid: "That code isn't right. [n] tries left.",
    verifyExpired: 'This code has expired. Tap Resend code to get a new one.',
    verifyLocked: 'Too many tries. Tap Resend code to get a new one.',
    verifySent: 'New code sent. Check your inbox and spam folder.',
    verifySendFailed: "We couldn't send the code. Please try again in a moment.",
    verifyResend: 'Resend code',
    verifyResendIn: 'Resend code in [s]s',
    ...readJSON($('[data-hw-config]'), {})
  };
  const segments = $$('[data-hw-segment]').map((node) => node.dataset.prize);
  const segmentAngle = 360 / (segments.length || 1);

  const messageNode = $('[data-hw-msg]');
  const noticeNode = $('[data-hw-notice]');
  const spinsLeftNode = $('[data-hw-left]');
  const stage = $('[data-hw-stage]');
  const wheelNode = $('[data-hw-wheel]');
  const spinSection = $('[data-hw-spin]');
  const shoesSection = $('[data-hw-shoes]');
  const extraSection = $('[data-hw-extra]');
  const shareButton = $('[data-hw-share]');
  const shareMessageNode = $('[data-hw-share-msg]');
  const shareDialog = $('[data-hw-share-dialog]');
  const shareUrlField = $('[data-hw-share-url]');
  const shareStatusNode = $('[data-hw-share-status]');
  const shareApps = $$('[data-hw-share-app]');
  const cardTitle = $('[data-hw-card-title]');
  const cardPrize = $('[data-hw-card-prize]');
  const cardLive = $('[data-hw-card-live]');
  const cardShop = $('[data-hw-card-shop]');
  const cardShopLink = cardShop?.closest('a');
  const gift = $('[data-hw-gift]');
  const giftGlow = $('.hw-gift__glow');
  const giftClosed = $('[data-hw-gift-closed]');
  const giftOpen = $('[data-hw-gift-open]');
  const cardAgain = $('[data-hw-card-again]');
  const countdown = { h: $('[data-hw-cd="h"]'), m: $('[data-hw-cd="m"]'), s: $('[data-hw-cd="s"]') };

  const state = {
    result: null, // { email, prize, code, expiresAt }
    expired: false,
    spinning: false,
    checking: false, // 本地记的次数为 0，正在向服务确认
    closed: false, // 服务地址为空，或活动窗口未开 / 已关
    cardOpen: false,
  };
  // 已知的剩余次数（按邮箱）。服务端才是事实来源，这里只用来在记的是 0 时先问一声，不白转一次
  const spinsLeft = new Map();

  const fill = (template, prize) => String(template || '').replace('[prize]', PRIZES[prize] ?? '');
  const emailKey = (email) => email.trim().toLowerCase();
  // 「Spins Remaining」显示的是最近一次点 SPIN TO WIN（或刷新后恢复）所用邮箱的次数，不跟着输入框实时变：
  // 用完次数后在框里换个新邮箱，不该马上显示 1，要等点了 SPIN TO WIN、向服务确认过才更新（用户 2026-10-15）。
  // 还没点过 SPIN 时显示 0
  let shownEmail = '';
  // 当前邮箱的次数一变成 0，分享卡里就直接提示「今天的次数用完了，分享可再得一次」，不用等顾客再去点转盘；
  // 次数恢复（换了邮箱、好友打开了链接）就收起。只管这一句，不覆盖分享时的其它提示和报错
  const renderSpins = () => {
    const known = shownEmail ? spinsLeft.get(emailKey(shownEmail)) : undefined;
    if (shareMessageNode) {
      const current = shareMessageNode.textContent;
      if (known === 0 && !current) setMessage(shareMessageNode, text.noSpins, false);
      else if (known !== 0 && current === text.noSpins) setMessage(shareMessageNode, '', false);
    }
    if (!spinsLeftNode) return;
    // 还没点过 SPIN（没有邮箱）时显示 0，不显示 1（用户 2026-10-15）；点了 SPIN、这个邮箱还没向服务确认过时按 1
    spinsLeftNode.textContent = String(Number.isFinite(known) ? known : shownEmail ? 1 : 0);
  };
  const rememberSpins = (email, value) => {
    if (!Number.isFinite(value)) return;
    spinsLeft.set(emailKey(email), value);
    renderSpins();
  };

  const setMessage = (node, message, isError) => {
    if (!node) return;
    node.textContent = message || '';
    node.classList.toggle('is-error', Boolean(message) && isError);
  };
  const say = (message, isError = false) => setMessage(messageNode, message, isError);
  const shareSay = (message, isError = false) => setMessage(shareMessageNode, message, isError);

  const syncSpinButton = () => {
    const disabled = state.closed || state.spinning || state.checking;
    spinButtons.forEach((button) => {
      button.disabled = disabled;
    });
    // 转动中只是暂时点不了，样式里据此不置灰
    root.classList.toggle('is-spinning', state.spinning || state.checking);
  };

  // why：not_started / ended，埋点用来区分抽奖结果；占位文案（服务地址为空）不带
  let closedAs = '';
  const closeWheel = (message, why = '') => {
    state.closed = true;
    closedAs = why;
    if (noticeNode) {
      noticeNode.textContent = message || '';
      noticeNode.hidden = !message;
    }
    syncSpinButton();
  };

  /* ------------------------------------------------------------ 本地存储 */
  const isResult = (value) =>
    Boolean(value) &&
    typeof value.email === 'string' &&
    typeof value.code === 'string' &&
    value.code !== '' &&
    isPrize(value.prize) &&
    Number.isFinite(Date.parse(value.expiresAt));

  // 隐私模式等场景下 localStorage 会抛错：读不到就当没中过，写不进就只在本次页面里有效
  const store = {
    read() {
      try {
        const value = JSON.parse(window.localStorage.getItem(STORE_KEY));
        return isResult(value) ? value : null;
      } catch (error) {
        return null;
      }
    },
    write(value) {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(value));
      } catch (error) {
        /* 存储不可用时页面照常工作 */
      }
    },
    clear() {
      try {
        window.localStorage.removeItem(STORE_KEY);
      } catch (error) {
        /* 同上 */
      }
    }
  };

  const refStore = {
    read() {
      try {
        const value = JSON.parse(window.localStorage.getItem(REF_KEY));
        return value && typeof value.email === 'string' && REF_RE.test(value.ref) ? value : null;
      } catch (error) {
        return null;
      }
    },
    write(value) {
      try {
        window.localStorage.setItem(REF_KEY, JSON.stringify(value));
      } catch (error) {
        /* 存不进只影响「打开自己的链接不登记」这一条，服务端每天也只给一次 */
      }
    }
  };

  // 存不进（隐私模式）就只在本次页面里计
  const shareCount = {
    memory: null,
    today() {
      try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
      } catch (error) {
        return new Date().toISOString().slice(0, 10);
      }
    },
    read() {
      let value = this.memory;
      try {
        value = JSON.parse(window.localStorage.getItem(SHARE_COUNT_KEY)) || value;
      } catch (error) {
        /* 用内存里的 */
      }
      return value && value.date === this.today() && Number.isFinite(value.n) ? value.n : 0;
    },
    add() {
      this.memory = { date: this.today(), n: this.read() + 1 };
      try {
        window.localStorage.setItem(SHARE_COUNT_KEY, JSON.stringify(this.memory));
      } catch (error) {
        /* 只在本次页面里有效 */
      }
    }
  };

  /* ------------------------------------------------------------ 抽奖服务 */
  // 失败统一抛出带 code 的错误：服务端的 error 字段原样透传，网络 / 超时 / 非 JSON 归为 network 或 server_error
  const call = async (method, path, body) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT) : 0;
    let response;
    try {
      response = await fetch(`${api}${path}`, {
        method,
        mode: 'cors',
        credentials: 'omit',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller?.signal
      });
    } catch (error) {
      throw Object.assign(new Error('network'), { code: 'network' });
    } finally {
      clearTimeout(timer);
    }
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) {
      // data 一并带上：验证码相关的错误里有 resendIn / triesLeft
      throw Object.assign(new Error('spin service'), { code: data?.error || 'server_error', data: data || {} });
    }
    return data;
  };

  // 存不进浏览器（隐私模式等）时每次打开页面换一个，服务端仍有 IP 规则兜底
  const deviceId = (() => {
    const valid = (value) => /^[A-Za-z0-9-]{8,64}$/.test(value || '');
    const fresh = () =>
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      const saved = window.localStorage.getItem(DEVICE_KEY);
      if (valid(saved)) return saved;
      const id = fresh();
      window.localStorage.setItem(DEVICE_KEY, id);
      return id;
    } catch (error) {
      return fresh();
    }
  })();

  /* ------------------------------------------------------------ 转盘动画 */
  const wheel = (() => {
    let angle = 0;
    let frame = 0;
    let last = 0;

    // 格子文字在盘面里，跟着盘面一起转
    const paint = () => {
      disc.style.transform = `rotate(${angle}deg)`;
    };

    const cruise = (now) => {
      // 标签页切到后台再回来时帧间隔会很大，封顶避免盘面瞬移
      angle += (CRUISE_SPEED * Math.min(now - last, 100)) / 1000;
      last = now;
      paint();
      frame = window.requestAnimationFrame(cruise);
    };

    // 三次缓出到目标角度。时长取 3 × 距离 / 当前速度，使起始速度等于匀速段的速度，衔接处不顿挫
    const easeTo = (target) =>
      new Promise((resolve) => {
        window.cancelAnimationFrame(frame);
        const from = angle;
        const distance = target - from;
        const duration = (3 * distance * 1000) / CRUISE_SPEED;
        const startedAt = window.performance.now();
        const step = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          angle = from + distance * (1 - (1 - progress) ** 3);
          paint();
          if (progress < 1) {
            frame = window.requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        frame = window.requestAnimationFrame(step);
      });

    // 指针在正上方，第 index 格的中心初始位于顺时针 index × 格角处，盘面转到 -index × 格角 时该格对准指针
    const restAngle = (index, offset) => mod(-index * segmentAngle + offset, 360);

    return {
      start() {
        if (reducedMotion()) return;
        window.cancelAnimationFrame(frame);
        last = window.performance.now();
        frame = window.requestAnimationFrame(cruise);
      },
      // 再转至少 MIN_TRAVEL 度后停在第 index 格；距离 180～540°，按上面的时长公式减速段 0.3～0.9 秒
      land(index) {
        // 停在中奖格正中（用户 2026-10-15），指针正对格子中线
        const want = restAngle(index, 0);
        if (reducedMotion()) {
          angle = want;
          paint();
          return Promise.resolve();
        }
        const base = angle + MIN_TRAVEL;
        return easeTo(base + mod(want - base, 360));
      },
      // 抽奖失败：约 1.2 秒内缓停在任意位置
      abort() {
        if (reducedMotion()) return Promise.resolve();
        return easeTo(angle + (CRUISE_SPEED * 1.2) / 3);
      },
      set(index) {
        angle = restAngle(index, 0);
        paint();
      }
    };
  })();

  // 10 和 15 各有两格，随机取一格
  const segmentFor = (prize) => {
    const candidates = segments.reduce((list, value, index) => (value === prize ? [...list, index] : list), []);
    return candidates.length ? candidates[Math.floor(Math.random() * candidates.length)] : -1;
  };

  /* ------------------------------------------------------------ 中奖卡、个人倒计时 */
  const timeLeft = () => (state.result ? Date.parse(state.result.expiresAt) - Date.now() : 0);

  // 中奖倒计时（移动端置顶条 / 网页端右下角悬浮卡）：中奖卡只在开箱后弹出一次，关掉后码和剩余时间都靠它显示。
  // 有未过期的码、中奖卡没开着、也不在开箱动画中时显示
  const sticky = $('[data-hw-sticky]');
  const stickyPrize = $('[data-hw-sticky-prize]');
  const stickyCountdown = { h: $('[data-hw-sticky-cd="h"]'), m: $('[data-hw-sticky-cd="m"]'), s: $('[data-hw-sticky-cd="s"]') };
  // 中奖倒计时只在活动倒计时卡已经整张滚过页头之后出现（用户 2026-10-15）：
  // 活动倒计时还在屏幕上时不出，免得两个倒计时同时出现；还没滑到它（卡在屏幕下方）时也不出，否则置顶条会压住首屏标题。
  // 往回滚、卡片从页头下面露出一点就收起
  const campaignBox = $('[data-hw-campaign]');
  // --hw-header 是 calc(…) 表达式，读变量拿不到像素：用一个高度取该变量的隐形元素换算
  const headerProbe = doc.createElement('div');
  headerProbe.setAttribute('aria-hidden', 'true');
  headerProbe.style.cssText = 'position:absolute;top:0;left:0;width:0;visibility:hidden;pointer-events:none;height:var(--hw-header)';
  root.append(headerProbe);
  // 倒计时条停稳后的位置（隐藏时量不到：同步地临时显示一下再量，同一帧内恢复，不会画出来）。
  // 用 offsetTop 而不是 getBoundingClientRect：入场动画从 translateY(-100%) 滑入，按包围盒量会差一整条，
  // 刚显示的条下一帧就被判成没滑过、又藏起来。条是 position: fixed，offsetTop 即距视口顶的距离
  const stickyRect = () => {
    const wasHidden = sticky.hidden;
    sticky.hidden = false;
    const top = sticky.offsetTop;
    const bottom = top + sticky.offsetHeight;
    sticky.hidden = wasHidden;
    return { top, bottom };
  };
  // 移动端条贴在页头下方：活动倒计时只剩条能盖住的那一截时就算滑过，条出现正好把它盖住
  // （首屏按钮滚到 How To Play 时标题停在条下方，用户 2026-10-16）；网页端条在右下角，要整张滑到页头后面
  const campaignPassed = () => {
    if (!campaignBox) return true;
    const rect = campaignBox.getBoundingClientRect();
    if (rect.height === 0) return true;
    let line = headerProbe.offsetHeight;
    if (sticky) {
      const bar = stickyRect();
      if (bar.top < window.innerHeight / 2) line = Math.max(line, bar.bottom);
    }
    return rect.bottom <= line;
  };
  const updateSticky = () => {
    if (!sticky) return;
    sticky.hidden = !(state.result && !state.expired && card.hidden && (!gift || gift.hidden) && campaignPassed());
  };
  let stickyQueued = false;
  const queueSticky = () => {
    if (stickyQueued) return;
    stickyQueued = true;
    window.requestAnimationFrame(() => {
      stickyQueued = false;
      if (state.result) updateSticky();
    });
  };
  window.addEventListener('scroll', queueSticky, { passive: true });
  window.addEventListener('resize', queueSticky);
  // 奖项拆成大小两段：「15%」大字、「OFF」小字（BOGO FREE 同理）
  const renderStickyPrize = (prize) => {
    if (!stickyPrize) return;
    const [lead, ...rest] = String(PRIZES[prize] ?? '').split(' ');
    const big = doc.createElement('span');
    big.className = 'hw-sticky__big';
    big.textContent = lead;
    const small = doc.createElement('span');
    small.className = 'hw-sticky__small';
    small.textContent = rest.join(' ');
    stickyPrize.replaceChildren(big, ' ', small);
    if (sticky) sticky.dataset.prize = prize;
  };

  const renderCard = () => {
    if (!state.result) return;
    const { prize } = state.result;
    cardTitle.textContent = state.expired ? text.cardExpiredTitle || '' : fill(text.cardTitle, prize);
    if (cardPrize) {
      cardPrize.textContent = PRIZES[prize] ?? '';
      cardPrize.hidden = state.expired;
    }
    card.dataset.prize = prize;
    card.classList.toggle('is-expired', state.expired);
    if (cardLive) cardLive.hidden = state.expired;
    if (cardAgain) cardAgain.hidden = !state.expired;
    if (cardShop) cardShop.textContent = fill(text.cardButton, prize);
    renderStickyPrize(prize);
    updateSticky();
  };

  // 剩余时间始终用到期时间减当前时间，刷新后自然从剩余时间继续
  const renderTime = () => {
    if (!state.result) return;
    const left = timeLeft();
    if (left <= 0) {
      if (!state.expired) {
        state.expired = true;
        renderCard();
        // 没开着中奖卡时过期了：结果直接丢掉，下次打开页面不再显示
        if (card.hidden) {
          state.result = null;
          store.clear();
          updateSticky();
        }
      }
      return;
    }
    const total = Math.floor(left / 1000);
    const hours = pad(Math.floor(total / 3600));
    const minutes = pad(Math.floor((total % 3600) / 60));
    const seconds = pad(total % 60);
    if (countdown.h) countdown.h.textContent = hours;
    if (countdown.m) countdown.m.textContent = minutes;
    if (countdown.s) countdown.s.textContent = seconds;
    if (stickyCountdown.h) stickyCountdown.h.textContent = hours;
    if (stickyCountdown.m) stickyCountdown.m.textContent = minutes;
    if (stickyCountdown.s) stickyCountdown.s.textContent = seconds;
  };

  let clock = 0;
  const setResult = (result) => {
    state.result = result;
    state.expired = timeLeft() <= 0;
    renderCard();
    renderTime();
    if (!clock) clock = window.setInterval(renderTime, 1000);
  };

  // 中奖弹窗和礼盒按设备等比缩放（用户 2026-10-14）：设计稿卡片 360 × 450，
  // 弹窗取「屏宽 92%」「屏高 82%」里能放下的最大倍数，最多放大到 1.4；收回到转盘位置的卡片只缩不放
  const fitPopups = () => {
    const width = doc.documentElement.clientWidth;
    const height = window.innerHeight;
    const pop = Math.min((width * 0.92) / 360, (height * 0.82) / 450, 1.4);
    const dock = Math.min(1, (width - 32) / 360);
    root.style.setProperty('--hw-pop-s', pop.toFixed(3));
    root.style.setProperty('--hw-dock-s', dock.toFixed(3));
    // 移动端置顶倒计时按设计稿 393 宽排版，更窄的屏幕整条等比缩小
    root.style.setProperty('--hw-bar-s', Math.min(1, width / 393).toFixed(3));
  };
  fitPopups();
  window.addEventListener('resize', fitPopups);

  // 3D 翻转出场（用户 2026-10-14）：卡片先竖着、和屏幕垂直（只看得到一条竖线），绕竖轴翻到正对屏幕，
  // 稍微翻过头再回正；翻到一半后内容由上到下依次浮现
  const unrollCard = () => {
    const paper = card.firstElementChild;
    if (reducedMotion() || !paper?.animate) return;
    const flip = 900;
    paper.animate(
      [
        { transform: 'perspective(1000px) rotateY(90deg)', opacity: 0.6, offset: 0 },
        { transform: 'perspective(1000px) rotateY(-10deg)', opacity: 1, offset: 0.7 },
        { transform: 'perspective(1000px) rotateY(0deg)', opacity: 1 }
      ],
      { duration: flip, easing: 'cubic-bezier(0.25, 0.8, 0.3, 1)', fill: 'backwards' }
    );
    $$('.hw-card__title > *, .hw-card__note, .hw-cd, .hw-btn, .hw-card__hint', paper)
      .filter((node) => node.offsetParent)
      .forEach((node, index) => {
        node.animate(
          [
            { opacity: 0, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'none' }
          ],
          { duration: 300, delay: flip * 0.45 + index * 60, easing: 'ease-out', fill: 'backwards' }
        );
      });
  };

  const openCard = ({ focus = false, unroll = false } = {}) => {
    if (!state.result) return;
    renderCard();
    renderTime();
    card.hidden = false;
    stage?.classList.add('is-won');
    // 卡片盖住转盘时，盘上的 SPIN 不该还能被键盘或读屏点到
    if (wheelNode) wheelNode.inert = true;
    state.cardOpen = true;
    // 中奖卡只以弹窗形式出现在屏幕正中（带遮罩），和开箱动画同一位置；关掉后不再常驻转盘前面
    card.classList.toggle('is-modal', unroll);
    updateSticky();
    if (unroll) unrollCard();
    if (focus) (state.expired ? cardAgain : cardShopLink)?.focus({ preventScroll: true });
  };

  /* ------------------------------------------------------------ 中奖开箱动画 */
  // 礼盒图懒加载，点 SPIN 时才让它下载，转盘转的几秒里就下好了
  const preloadGift = () => {
    [giftClosed, giftOpen].forEach((img) => {
      if (img) img.loading = 'eager';
    });
    // 中奖卡的票券底图是 CSS 背景，卡片显示时才会去下载：转盘转着的时候先下好，卡片出来不会先空白一下
    const ticket = /url\(['"]?([^'")]+)/.exec(getComputedStyle(root).getPropertyValue('--hw-ticket-img'));
    if (ticket) new Image().src = ticket[1];
  };

  // 图没下好（慢网、被拦）就不播，直接出卡片，最多等 1.5 秒
  const giftReady = () => {
    if (!gift || !giftGlow || !giftClosed || !giftOpen || !gift.animate) return Promise.resolve(false);
    const decoded = Promise.all([giftClosed.decode(), giftOpen.decode()]).then(
      () => true,
      () => false
    );
    return Promise.race([decoded, wait(1500).then(() => false)]);
  };

  // 合上的礼盒落下 → 摇晃 → 换成打开的礼盒、光晕放大 → 礼盒和光晕散开消失。点一下礼盒跳过
  const playGift = async () => {
    if (reducedMotion() || !(await giftReady())) return;
    let skipped = false;
    let fading = null;
    const running = [];
    const run = (node, frames, options) => {
      if (skipped) return Promise.resolve();
      const animation = node.animate(frames, { fill: 'both', ...options });
      running.push(animation);
      return animation.finished.catch(() => {});
    };
    const skip = () => {
      skipped = true;
      running.forEach((animation) => animation.finish());
    };
    // 礼盒固定在屏幕正中（CSS 的 position: fixed），网页端也不跟着右栏的转盘走（用户 2026-10-14）
    gift.hidden = false;
    updateSticky();
    gift.addEventListener('click', skip);
    try {
      run(giftGlow, [{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 0.85, transform: 'none' }], {
        duration: 450,
        easing: 'ease-out'
      });
      await run(
        giftClosed,
        [
          { opacity: 0, transform: 'translateY(-48px) scale(0.4)' },
          { opacity: 1, transform: 'translateY(0) scale(1.06)', offset: 0.7 },
          { opacity: 1, transform: 'none' }
        ],
        { duration: 520, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' }
      );
      await run(
        giftClosed,
        [
          { transform: 'none' },
          { transform: 'rotate(-7deg) scale(1.02)' },
          { transform: 'rotate(6deg) scale(1.04)' },
          { transform: 'rotate(-5deg) scale(1.05)' },
          { transform: 'rotate(4deg) scale(1.06)' },
          { transform: 'scale(1.08)' }
        ],
        { duration: 560, easing: 'ease-in-out' }
      );
      run(giftClosed, [{ opacity: 1, transform: 'scale(1.08)' }, { opacity: 0, transform: 'scale(1.08)' }], {
        duration: 100
      });
      // 打开的礼盒这一帧（弹出 + 停留）缩短一半：480 + 380 → 240 + 190（用户 2026-10-15）
      const OPEN_IN = 240;
      const OPEN_HOLD = 190;
      run(giftGlow, [{ opacity: 0.85, transform: 'none' }, { opacity: 1, transform: 'scale(1.4)' }], {
        duration: OPEN_IN,
        easing: 'ease-out'
      });
      await run(
        giftOpen,
        [
          { opacity: 0, transform: 'scale(0.9)' },
          { opacity: 1, transform: 'scale(1.05)', offset: 0.45 },
          { opacity: 1, transform: 'none' }
        ],
        { duration: OPEN_IN, easing: 'ease-out' }
      );
      // 礼盒和光晕往中心缩小消失（用户 2026-10-15，原来是放大散开），缩到一半多时中奖卡从同一中心翻出来：
      // 卡片刚开始翻时是一条竖线，露出后面还在缩的礼盒，看起来是礼盒缩进去、卡片翻出来
      const SHRINK = 360;
      run(giftGlow, [{ opacity: 1, transform: 'scale(1.4)' }, { opacity: 0, transform: 'scale(0.2)' }], {
        duration: SHRINK,
        delay: OPEN_HOLD,
        easing: 'ease-in'
      });
      fading = run(
        giftOpen,
        [
          { opacity: 1, transform: 'none' },
          { opacity: 1, transform: 'scale(0.55)', offset: 0.6 },
          { opacity: 0, transform: 'scale(0.15)' }
        ],
        { duration: SHRINK, delay: OPEN_HOLD, easing: 'ease-in' }
      );
      await Promise.race([fading, wait(OPEN_HOLD + 220)]);
    } finally {
      const cleanup = () => {
        gift.removeEventListener('click', skip);
        gift.hidden = true;
        updateSticky();
        running.forEach((animation) => animation.cancel());
      };
      if (fading && !skipped) fading.then(cleanup);
      else cleanup();
    }
  };

  // 点遮罩 / Esc / 去选鞋：关掉中奖弹窗（码保留，之后由倒计时显示）
  // reason 是埋点的关闭方式（shop / backdrop / esc / close / spin_again），只在卡片确实开着时报
  const dockCard = (reason) => {
    if (state.cardOpen) closeCard(reason);
  };

  const closeCard = (reason) => {
    if (state.cardOpen && typeof reason === 'string') track('hw_card_action', { action: reason });
    const hadFocus = card.contains(doc.activeElement);
    card.classList.remove('is-modal');
    card.hidden = true;
    stage?.classList.remove('is-won');
    if (wheelNode) wheelNode.inert = false;
    state.cardOpen = false;
    // 过期的结果关掉就丢弃，下次打开页面不再弹过期卡
    if (state.expired) {
      state.result = null;
      store.clear();
    }
    updateSticky();
    if (hadFocus) (spinButton.disabled ? emailInput : spinButton).focus({ preventScroll: true });
  };

  // 带码：请求一次 /discount/<码>，Shopify 把码写进会话；失败不提示，顾客仍可在结算页手动输入邮件里的码
  const applyCode = () => {
    if (!state.result || state.expired) return;
    fetch(`${routeRoot()}discount/${encodeURIComponent(state.result.code)}`, { credentials: 'same-origin' }).catch(() => {});
  };

  // 等 Copy Link 真正滚进视野再开始跳，滚动途中不跳；不支持 IntersectionObserver 的就直接跳
  let pulseWatch = null;
  const pulseWhenVisible = () => {
    if (!extraSection || !shareButton || extraSection.classList.contains('is-pulsing')) return;
    if (!('IntersectionObserver' in window)) {
      extraSection.classList.add('is-pulsing');
      return;
    }
    pulseWatch?.disconnect();
    pulseWatch = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        extraSection.classList.add('is-pulsing');
        pulseWatch.disconnect();
        pulseWatch = null;
      },
      { threshold: 1 }
    );
    pulseWatch.observe(shareButton);
  };

  /* ------------------------------------------------------------ 抽奖 */
  const outOfSpins = () => {
    say(text.noSpins, true);
    shareSay(text.noSpins);
    // 把视线引到分享按钮：高亮并滚到可见处，Copy Link 进入视野后开始持续放大缩小（is-pulsing）
    extraSection?.classList.add('is-nudged');
    centerInView(extraSection?.querySelector('.hw-h2') || extraSection, { alignTop: true });
    pulseWhenVisible();
  };

  // 返回埋点的 outcome
  const handleSpinError = (code) => {
    if (code === 'not_started' || code === 'ended') {
      closeWheel(code === 'ended' ? text.ended : text.notStarted, code);
      return code;
    }
    if (code === 'invalid_email') {
      say(text.emailInvalid, true);
      return 'email_invalid';
    }
    if (code === 'no_spins') {
      outOfSpins();
      return code;
    }
    say(text.error, true);
    return 'error';
  };

  const validEmail = () => {
    const email = emailInput.value.trim();
    return EMAIL_RE.test(email) ? email : '';
  };

  const applyWindow = (data) => {
    if (data.window === 'not_started') closeWheel(text.notStarted, 'not_started');
    else if (data.window === 'ended') closeWheel(text.ended, 'ended');
  };

  // 本地记的 0 可能已经过时：分享者停在页面上，好友打开链接后服务端多了一次。
  // 确认期间禁用 SPIN 防连点；请求失败就沿用本地的 0
  const confirmSpins = async (email) => {
    state.checking = true;
    syncSpinButton();
    try {
      const data = await call('GET', `/status?email=${encodeURIComponent(email)}`);
      applyWindow(data);
      rememberSpins(email, data.spinsLeft);
    } catch (error) {
      /* 沿用本地记录 */
    }
    state.checking = false;
    syncSpinButton();
    return spinsLeft.get(emailKey(email)) > 0;
  };

  // 每次被受理的提交以且仅以一条 hw_spin_result 结束；开头被忽略的重复提交不报
  const spinResult = (outcome, params = {}) => track('hw_spin_result', { outcome, ...params });

  // 转盘居中在「没被遮住的那段」里，不是整个视口：上面扣掉固定页头，已有码时还要扣掉移动端置顶倒计时条
  // （网页端是右下角卡片，就扣掉底部）。只按视口居中时，真机上置顶条会压住转盘上沿（用户 2026-10-15 真机反馈）
  // 转盘和「没次数时滚到的分享卡」共用（用户 2026-10-15：分享卡要整张出现在屏幕上，不被倒计时条挡住）
  // alignTop：不居中，把 node 顶边放到可见区上沿下方 16（没次数时滚到「Unlock Extra Rewards」标题，用户 2026-10-15）
  const centerInView = (node, { alignTop = false } = {}) => {
    if (!node) return;
    let top = headerProbe.offsetHeight;
    let bottom = window.innerHeight;
    // 点 SPIN 时活动倒计时多半还在屏幕上、倒计时条是隐藏的，滚到转盘后它才出现：有有效的码就按它会出现来留位置。
    if (sticky && state.result && !state.expired) {
      const bar = stickyRect();
      if (bar.top < bottom / 2) top = Math.max(top, bar.bottom);
      else bottom = Math.min(bottom, bar.top);
    }
    top += alignTop ? 16 : 8;
    const rect = node.getBoundingClientRect();
    const room = bottom - top;
    const target = !alignTop && rect.height < room ? top + (room - rect.height) / 2 : top;
    window.scrollBy({ top: rect.top - target, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  /* ------------------------------------------------------------ 防刷验证码 */
  // 服务端判定这次提交可疑（同一 IP / 设备短时间换了好几个邮箱）时回 verify_required 并给该邮箱发 6 位码，
  // 这里弹窗让顾客填码，填对后同一次请求里直接抽奖（用户 2026-10-16）
  const verifyDialog = $('[data-hw-verify]');
  const verifyForm = $('[data-hw-verify-form]');
  const verifyInput = $('[data-hw-verify-input]');
  const verifyEmailNode = $('[data-hw-verify-email]');
  const verifyMsg = $('[data-hw-verify-msg]');
  const verifySubmit = $('[data-hw-verify-submit]');
  const verifyResend = $('[data-hw-verify-resend]');
  const verifySay = (message, isError = false) => setMessage(verifyMsg, message, isError);
  let verifyEmail = '';
  let resendAt = 0;
  let resendTimer = 0;

  const renderResend = () => {
    if (!verifyResend) return;
    const left = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
    verifyResend.disabled = left > 0 || state.spinning;
    verifyResend.textContent = left > 0 ? text.verifyResendIn.replace('[s]', left) : text.verifyResend;
    if (!left) {
      window.clearInterval(resendTimer);
      resendTimer = 0;
    }
  };
  const startResendClock = (seconds) => {
    resendAt = Date.now() + Math.max(0, Number(seconds) || 0) * 1000;
    window.clearInterval(resendTimer);
    resendTimer = window.setInterval(renderResend, 1000);
    renderResend();
  };

  const openVerify = (email, resendIn, message = '') => {
    if (!verifyDialog) return say(text.verifySendFailed, true);
    verifyEmail = email;
    if (verifyEmailNode) verifyEmailNode.textContent = email;
    startResendClock(resendIn);
    verifySay(message);
    if (!verifyDialog.open) {
      if (verifyInput) verifyInput.value = '';
      if (typeof verifyDialog.showModal === 'function') verifyDialog.showModal();
      else verifyDialog.setAttribute('open', '');
    }
    verifyInput?.focus();
  };
  const closeVerify = () => {
    window.clearInterval(resendTimer);
    resendTimer = 0;
    if (!verifyDialog?.open) return;
    if (typeof verifyDialog.close === 'function') verifyDialog.close();
    else verifyDialog.removeAttribute('open');
  };

  // 服务端验证码相关的回复：弹窗里提示，返回埋点 outcome
  const handleVerify = (code, data, email, resend) => {
    if (code === 'verify_required') {
      // 重发且服务端确实发了新码（冷却满 60 秒才会回 60）才提示已重发
      openVerify(email, data.resendIn, resend && data.resendIn >= 60 ? text.verifySent : '');
    } else if (code === 'verify_invalid') {
      verifySay(text.verifyInvalid.replace('[n]', data.triesLeft ?? ''), true);
      verifyInput?.select();
    } else if (code === 'verify_expired') {
      verifySay(text.verifyExpired, true);
      startResendClock(0);
    } else if (code === 'verify_locked') {
      verifySay(text.verifyLocked, true);
    } else if (verifyDialog?.open) {
      verifySay(text.verifySendFailed, true);
    } else {
      say(text.verifySendFailed, true);
    }
    return code;
  };

  // code：弹窗里填的验证码；resend：弹窗里点了重发。这两种情况邮箱就是正在验证的那个，
  // 转盘等服务端放行了才开始转，填错码时不会先转一下又停
  const spin = async ({ code = '', resend = false } = {}) => {
    if (state.spinning || state.checking || state.closed) return;
    const viaVerify = Boolean(code || resend) && Boolean(verifyEmail);
    if (viaVerify) return spinWith(verifyEmail, { code, resend });

    const typed = emailInput.value.trim();
    if (!typed) {
      say(text.emailEmpty, true);
      emailInput.focus();
      spinResult('email_empty');
      return;
    }
    const email = validEmail();
    if (!email) {
      say(text.emailInvalid, true);
      emailInput.focus();
      spinResult('email_invalid');
      return;
    }
    const suggestion = suggestEmail(email);
    if (suggestion) {
      say(text.emailTypo.replace('[email]', suggestion), true);
      emailInput.focus();
      spinResult('email_typo');
      return;
    }
    // 没勾同意就不往下走：后面的次数确认和抽奖都要请求服务
    if (consentBox && !consentBox.checked) {
      say(text.consentRequired, true);
      consentBox.focus();
      spinResult('consent_missing');
      return;
    }
    // 从这里起次数显示改看这个邮箱（换了新邮箱时，下面确认 / 抽奖的结果会写进来）
    shownEmail = email;
    renderSpins();
    if (spinsLeft.get(emailKey(email)) === 0) {
      const confirmed = await confirmSpins(email);
      if (state.closed) {
        spinResult(closedAs || 'error');
        return;
      }
      if (!confirmed) {
        outOfSpins();
        spinResult('no_spins');
        return;
      }
    }

    return spinWith(email);
  };

  const spinWith = async (email, { code = '', resend = false } = {}) => {
    const viaVerify = Boolean(code || resend);
    // 卡片开着时在邮箱框里按回车也会走到这里：先收起卡片，让顾客看得到转盘
    if (state.cardOpen) closeCard('spin_again');
    state.spinning = true;
    syncSpinButton();
    say('');
    shareSay('');
    preloadGift();
    if (viaVerify) {
      if (verifySubmit) verifySubmit.disabled = true;
      renderResend();
    } else {
      // 表单在转盘上方（移动端）或旁边：先把转盘滚到屏幕正中，转动、开箱、中奖卡都在视线中央
      centerInView(stage);
      wheel.start();
    }

    let data = null;
    let errorCode = '';
    let errorData = {};
    try {
      data = await call('POST', '/spin', { email, device: deviceId, ...(code ? { code } : {}) });
      data = { ...data, prize: String(data.prize) };
      // 响应不合契约（未知奖项、缺码、到期时间不是日期）一律按服务出错处理，不猜
      if (!isResult({ ...data, email })) errorCode = 'server_error';
    } catch (error) {
      errorCode = error.code || 'network';
      errorData = error.data || {};
    }

    const index = errorCode ? -1 : segmentFor(data.prize);
    if (errorCode || index < 0) {
      if (!viaVerify) await wheel.abort();
      state.spinning = false;
      syncSpinButton();
      if (verifySubmit) verifySubmit.disabled = false;
      renderResend();
      if (errorCode.startsWith('verify_')) {
        spinResult(handleVerify(errorCode, errorData, email, resend));
        return;
      }
      closeVerify();
      if (errorCode === 'no_spins') rememberSpins(email, 0);
      spinResult(handleSpinError(errorCode || 'server_error'));
      return;
    }

    // 验证通过：关掉弹窗，这时才开始转
    if (viaVerify) {
      closeVerify();
      if (verifySubmit) verifySubmit.disabled = false;
      verifyEmail = '';
      centerInView(stage);
      wheel.start();
    }

    // 码一拿到就落盘：动画还没播完时顾客刷新页面，也能恢复出这次的中奖结果
    const result = { email, prize: data.prize, code: data.code, expiresAt: data.expiresAt };
    store.write(result);
    rememberSpins(email, data.spinsLeft);
    // 落盘即报，不等动画播完（中途刷新也不丢）
    spinResult('won', { prize: data.prize, ...(Number.isFinite(data.spinsLeft) ? { spins_left: data.spinsLeft } : {}) });

    await wheel.land(index);
    await wait(300);
    setResult(result);
    // 开箱动画期间转盘已经压暗，SPIN 仍算转动中，不能再点
    stage?.classList.add('is-won');
    await playGift();
    state.spinning = false;
    syncSpinButton();
    openCard({ focus: true, unroll: true });
    // 转完就把分享标识取好，之后点分享不用再等请求（见 share 里关于点击手势的说明）
    if (refStore.read()?.email !== email) fetchRef(email).catch(() => {});
  };

  /* ------------------------------------------------------------ 分享专属链接换额外抽奖 */
  // 次数不在点分享时给：好友打开带 ref 的链接（见下面的 registerVisit），服务端才给分享者加一次
  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      // 旧浏览器或非安全上下文没有 Clipboard API：退回选中文本 + execCommand
      const field = doc.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      // 弹窗以模态打开时 body 下其余内容是 inert 的，选不中，所以挂到弹窗里
      (shareDialog?.open ? shareDialog : doc.body).appendChild(field);
      field.select();
      let copied = false;
      try {
        copied = doc.execCommand('copy');
      } catch (commandError) {
        copied = false;
      }
      field.remove();
      return copied;
    }
  };

  const fetchRef = async (email) => {
    const data = await call('POST', '/share', { email });
    const ref = String(data.ref ?? '');
    // 标识不合契约就不拼链接：发出去一条好友打开也不算数的链接比报错更糟
    if (!REF_RE.test(ref)) throw Object.assign(new Error('spin service'), { code: 'server_error' });
    refStore.write({ email, ref });
    rememberSpins(email, data.spinsLeft);
    return ref;
  };

  // 各聊天应用的打开地址。能预填的带上「文案 + 链接」；Messenger 和 Instagram 的网页入口不收预填内容，
  // 只负责把应用打开，顾客粘贴已复制的链接（手机上 Messenger 用应用自己的协议，能直接带链接）
  const APP_LINKS = {
    sms: (url, message) => `sms:?&body=${encodeURIComponent(message)}`,
    whatsapp: (url, message) => `https://wa.me/?text=${encodeURIComponent(message)}`,
    messenger: (url) =>
      window.matchMedia('(pointer: coarse)').matches
        ? `fb-messenger://share/?link=${encodeURIComponent(url)}`
        : 'https://www.messenger.com/new',
    instagram: () => 'https://www.instagram.com/direct/inbox/',
    facebook: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    snapchat: (url) => `https://www.snapchat.com/share?link=${encodeURIComponent(url)}`,
    pinterest: (url) =>
      `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text.shareText || '')}`
  };

  const canDialog = Boolean(shareDialog) && typeof shareDialog.showModal === 'function';
  const dialogSay = (message, isError = false) => setMessage(shareStatusNode, message, isError);

  let shareUrl = '';
  const setShareUrl = (url) => {
    shareUrl = url;
    if (shareUrlField) shareUrlField.value = url;
    const message = [text.shareText, url].filter(Boolean).join(' ');
    shareApps.forEach((link) => {
      const build = APP_LINKS[link.dataset.hwShareApp];
      if (url && build) {
        link.href = build(url, message);
        // 应用协议（sms: / fb-messenger:）不开新标签，否则会留下一个空白页
        if (link.protocol.startsWith('http')) link.target = '_blank';
        else link.removeAttribute('target');
      } else {
        link.removeAttribute('href');
      }
    });
  };

  // 链接 + Copy 一行平时隐藏：打开弹窗时链接已自动复制；剪贴板失败才显示出来让顾客手动复制
  const shareLinkRow = shareUrlField?.closest('.hw-share__link');
  // trigger：auto = 打开弹窗时自动复制，manual = 点 Copy；点应用时的补复制不传、不报
  const reportCopy = (copied, report = dialogSay, trigger = '') => {
    if (shareLinkRow && !copied) shareLinkRow.hidden = false;
    report(copied ? text.shareCopied : text.shareCopyFailed, !copied);
    if (trigger) track('hw_share', { action: 'copy', result: copied ? 'success' : 'fail', trigger });
  };

  const copyShareUrl = async (trigger = '') => {
    if (!shareUrl) return;
    reportCopy(await copyText(shareUrl), dialogSay, trigger);
  };

  let sharing = false;
  const share = async () => {
    if (sharing) return;
    const email = validEmail();
    if (!email) {
      track('hw_share', { action: 'open', blocked: 'need_email' });
      shareSay(text.shareNeedEmail, true);
      scrollToNode(spinSection);
      emailInput.focus({ preventScroll: true });
      return;
    }
    const suggestion = suggestEmail(email);
    if (suggestion) {
      track('hw_share', { action: 'open', blocked: 'email_typo' });
      shareSay(text.emailTypo.replace('[email]', suggestion), true);
      return;
    }
    // 服务地址为空（上线前的占位状态）：不算一次分享
    if (!api) {
      shareSay(text.warming);
      return;
    }
    if (shareCount.read() >= SHARE_PER_DAY) {
      track('hw_share', { action: 'open', blocked: 'daily_limit' });
      shareSay(text.shareLimit, true);
      return;
    }
    shareCount.add();
    track('hw_share', { action: 'open' });

    sharing = true;
    pulseWatch?.disconnect();
    extraSection?.classList.remove('is-nudged', 'is-pulsing');
    shareSay('');
    // 只取 origin + pathname：顾客自己可能就是从别人的 ?ref= 链接进来的，不能把那个标识带出去
    const linkFor = (ref) => `${window.location.origin}${window.location.pathname}?ref=${encodeURIComponent(ref)}`;
    // 不支持 <dialog> 的旧浏览器没有弹窗，只复制链接，结果写在按钮下方
    const report = canDialog ? dialogSay : shareSay;
    try {
      // 本地已有这个邮箱的标识就直接用：剪贴板要求仍在点击手势内，
      // iOS Safari 上等一次网络请求回来手势就失效了。标识对同一邮箱是固定的
      const cached = refStore.read();
      const known = cached?.email === email ? cached.ref : '';
      if (canDialog) {
        // 先开弹窗再取链接：没取到之前应用按钮没有 href，点不了
        if (shareLinkRow) shareLinkRow.hidden = true;
        setShareUrl(known ? linkFor(known) : '');
        dialogSay(known ? '' : text.shareLoading);
        shareDialog.showModal();
      }
      if (!known) setShareUrl(linkFor(await fetchRef(email)));
      reportCopy(await copyText(shareUrl), report, 'auto');
    } catch (error) {
      if (error.code === 'not_started') closeWheel(text.notStarted, 'not_started');
      else if (error.code === 'ended') closeWheel(text.ended, 'ended');
      report(text.error, true);
    } finally {
      sharing = false;
      // 点分享时清掉了提示；次数仍是 0 就把「次数用完」那句放回来
      renderSpins();
    }
  };

  // 好友带 ?ref= 打开页面：向服务登记一次，分享者因此多一次机会。访客这边不显示任何东西，地址栏也不动
  const registerVisit = () => {
    let ref = '';
    try {
      ref = new URLSearchParams(window.location.search).get('ref') || '';
    } catch (error) {
      return;
    }
    // 自己打开自己的链接不算
    if (!REF_RE.test(ref) || refStore.read()?.ref === ref) return;

    // 埋点单独记一个会话标记：登记失败会在本会话重试，到达只算一次。不带 ref 值
    const landedKey = `${VISIT_KEY}-landed:${ref}`;
    try {
      if (!window.sessionStorage.getItem(landedKey)) {
        window.sessionStorage.setItem(landedKey, '1');
        track('hw_referral_landing');
      }
    } catch (error) {
      track('hw_referral_landing');
    }

    const key = `${VISIT_KEY}:${ref}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
    } catch (error) {
      /* 读不到就照常登记，服务端每天只给分享者一次 */
    }
    // 登记成功才记：失败的那次留给本会话里下一次打开重试
    call('POST', '/visit', { ref })
      .then(() => {
        try {
          window.sessionStorage.setItem(key, '1');
        } catch (error) {
          /* 同上 */
        }
      })
      .catch(() => {});
  };

  // 活动日报（2026-10-16）：每次打开报一次，服务端按设备 + 30 分钟归并成会话。入口来源取链接上的 from
  // （首页 banner、页头、商详入口条由主题加上），带 ref 打开的算分享；读完把 from 从地址栏去掉，分享出去的链接不带它。
  // 服务端回 204 无正文，不走 call()；失败不重试，日报少算一次可以接受
  const registerHit = () => {
    let from = '';
    try {
      const url = new URL(window.location.href);
      from = url.searchParams.has('ref') ? 'share' : url.searchParams.get('from') || '';
      if (url.searchParams.has('from')) {
        url.searchParams.delete('from');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      /* 老浏览器没有 URL：来源记为 other */
    }
    fetch(`${api}/hit`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device: deviceId, from })
    }).catch(() => {});
  };

  /* ------------------------------------------------------------ 事件绑定 */
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    spin();
  });

  // 验证码弹窗：只收数字，填满 6 位自动提交（短信 / 邮件里复制粘贴过来也一样）
  const submitVerify = () => {
    const value = (verifyInput?.value || '').replace(/\D/g, '');
    if (value.length !== 6) {
      verifySay(text.verifyEnter, true);
      verifyInput?.focus();
      return;
    }
    spin({ code: value });
  };
  verifyForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    submitVerify();
  });
  verifyInput?.addEventListener('input', () => {
    const digits = verifyInput.value.replace(/\D/g, '').slice(0, 6);
    if (digits !== verifyInput.value) verifyInput.value = digits;
    if (digits.length === 6 && !state.spinning) submitVerify();
  });
  verifyResend?.addEventListener('click', () => spin({ resend: true }));
  $('[data-hw-verify-close]')?.addEventListener('click', closeVerify);
  verifyDialog?.addEventListener('click', (event) => {
    if (event.target === verifyDialog) closeVerify();
  });
  // 按 Esc 关闭时原生 dialog 不走 closeVerify，倒计时一并停掉
  verifyDialog?.addEventListener('close', () => {
    window.clearInterval(resendTimer);
    resendTimer = 0;
  });

  consentBox?.addEventListener('change', () => {
    if (consentBox.checked && messageNode?.textContent === text.consentRequired) say('');
  });

  $('[data-hw-card-close]')?.addEventListener('click', () => closeCard('close'));
  cardAgain?.addEventListener('click', () => closeCard('spin_again'));
  shareButton?.addEventListener('click', share);
  $('[data-hw-share-close]')?.addEventListener('click', () => shareDialog?.close());
  $('[data-hw-share-copy]')?.addEventListener('click', () => copyShareUrl('manual'));
  // 点弹窗以外的遮罩关闭：遮罩上的点击落在 dialog 元素自身
  shareDialog?.addEventListener('click', (event) => {
    if (event.target === shareDialog) shareDialog.close();
  });
  shareUrlField?.addEventListener('focus', () => shareUrlField.select());
  // 点应用时再复制一次（这次一定在点击手势内），不拦默认跳转
  shareApps.forEach((link) => {
    link.addEventListener('click', () => {
      if (link.hasAttribute('href')) copyShareUrl();
    });
  });

  $$('[data-hw-shop]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      applyCode();
      dockCard('shop');
      scrollToNode(shoesSection);
    });
  });

  // 屏幕正中的中奖弹窗：点遮罩、按 Esc 或去选鞋时收回到转盘位置（不丢结果）；点 × 才是关掉
  card.addEventListener('click', (event) => {
    if (event.target === card) dockCard('backdrop');
  });
  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') dockCard('esc');
  });

  $$('[data-hw-scroll]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      // 首屏按钮滚到「How To Play」标题（用户 2026-10-16）：标题顶到可见区上沿下方 16，
      // 有码时移动端的倒计时条滚过活动倒计时后会出现，按它的位置留出来，条刚好在标题上方
      const stepsTitle = $('[data-hw-steps] .hw-h2');
      if (stepsTitle) centerInView(stepsTitle, { alignTop: true });
      else scrollToNode(spinSection);
    });
  });

  /* ------------------------------------------------------------ 初始状态 */
  const saved = store.read();
  if (saved) {
    emailInput.value = saved.email;
    shownEmail = saved.email;
    const index = segments.indexOf(saved.prize);
    if (index >= 0) wheel.set(index);
    // 刷新后不再把中奖卡摆回转盘前面：码还有效就由倒计时显示，过期就丢掉
    setResult(saved);
    if (state.expired) {
      state.result = null;
      store.clear();
    }
    updateSticky();
  }

  if (!api) {
    closeWheel(text.warming);
    return;
  }
  syncSpinButton();
  registerVisit();
  registerHit();

  // 带上已知邮箱可以顺便拿到剩余次数。请求失败不禁用 SPIN：等顾客点的时候再报错
  call('GET', `/status${saved ? `?email=${encodeURIComponent(saved.email)}` : ''}`)
    .then((data) => {
      applyWindow(data);
      if (saved) rememberSpins(saved.email, data.spinsLeft);
    })
    .catch(() => {});
})();
